import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { AddOutlined, CenterFocusStrongOutlined, ExpandLessOutlined, ExpandMoreOutlined, RemoveOutlined, UnfoldLessOutlined, UnfoldMoreOutlined } from '@mui/icons-material';
import { Box, ButtonBase, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { designTokens } from '../tokens';
import { createFocusRingStyles } from '../interaction';
import { EmptyState } from './EmptyState';
import { visuallyHiddenStyles } from './visuallyHidden';

export type HierarchyChartNode = {
  id: string;
  parentId?: string;
  label: string;
  /** 이름과 별개로 노드의 상태나 추가 정보를 설명합니다. */
  description?: string;
  content?: ReactNode;
  footer?: ReactNode;
  searchText?: string;
  highlighted?: boolean;
};

export type HierarchyChartProps = {
  nodes: readonly HierarchyChartNode[];
  selectedId?: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  query?: string;
  /** 소비자가 필터링한 노드와 상위 경로를 접힘 상태와 관계없이 표시합니다. */
  filterActive?: boolean;
  /** 지정한 항목과 상위 경로를 펼치고 나머지 가지를 접습니다. 생략하면 사용자의 접힘 상태로 돌아갑니다. */
  focusedNodeIds?: readonly string[];
  revealId?: string;
  onMove?: (id: string, parentId: string) => void;
  canMove?: (id: string, parentId: string) => boolean;
  /** 도면 밖에서 끈 항목을 노드에 놓습니다. 데이터 해석과 업무 검증은 소비자가 소유합니다. */
  externalDrop?: {
    type: string;
    canDrop: (nodeId: string) => boolean;
    onDrop: (nodeId: string, data: string) => void;
  };
  /** 글자 크기는 유지하고 여백을 줄입니다. compact의 내용·하단 슬롯은 한 줄 구성을 권장합니다. */
  density?: 'standard' | 'compact';
  /** 부모가 제공한 남은 작업 공간을 채웁니다. */
  fill?: boolean;
  /** 처음 열 때 도면 전체를 맞춥니다. 수동 확대·축소 후에는 사용자가 정한 배율을 유지합니다. */
  defaultFit?: boolean;
};

const CHART_GEOMETRY = {
  standard: { cardWidth: 232, cardHeight: 192, columnGap: designTokens.space.xl, levelGap: 64, inset: designTokens.space.xxl },
  compact: { cardWidth: 224, cardHeight: 132, columnGap: designTokens.space.md, levelGap: 48, inset: designTokens.space.xl },
} as const;
const DRAG_TYPE = 'application/x-hierarchy-node';

/** 불완전한 계층도 노드를 보존하고 순환하는 연결만 분리합니다. */
function makeForest(nodes: readonly HierarchyChartNode[]) {
  const byId = new Map(nodes.map(node => [node.id, node]));
  const parents = new Map<string, string>();
  for (const node of nodes) {
    if (node.parentId && node.parentId !== node.id && byId.has(node.parentId)) parents.set(node.id, node.parentId);
  }
  for (const node of nodes) {
    const visited = new Set<string>([node.id]);
    let parent = parents.get(node.id);
    while (parent) {
      if (visited.has(parent)) { parents.delete(node.id); break; }
      visited.add(parent);
      parent = parents.get(parent);
    }
  }
  const children = new Map<string, string[]>();
  const roots: string[] = [];
  for (const node of byId.values()) {
    const parent = parents.get(node.id);
    if (!parent) roots.push(node.id);
    else children.set(parent, [...children.get(parent) ?? [], node.id]);
  }
  return { byId, parents, children, roots };
}

type Position = { id: string; x: number; y: number; depth: number };

/** 스크롤 가능한 도면과 노드 선택·펼침을 제공하며, 업무 상태와 변경 반영은 소비자가 소유합니다. */
export function HierarchyChart({ nodes, selectedId, onSelect, ariaLabel, query = '', filterActive = false, focusedNodeIds, revealId, onMove, canMove, externalDrop, density = 'standard', fill = false, defaultFit = false }: HierarchyChartProps) {
  const descriptionPrefix = useId();
  const { cardWidth, cardHeight, columnGap, levelGap, inset } = CHART_GEOMETRY[density];
  const compact = density === 'compact';
  const forest = useMemo(() => makeForest(nodes), [nodes]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [manualScale, setManualScale] = useState(1);
  const [fitToViewport, setFitToViewport] = useState(defaultFit);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const viewportWidth = viewportSize.width;
  const [draggingId, setDraggingId] = useState<string>();
  const [dropId, setDropId] = useState<string>();
  const [focusedId, setFocusedId] = useState<string>();
  const [panning, setPanning] = useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const pan = useRef<{ pointerId: number; x: number; y: number; left: number; top: number } | null>(null);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const focusMode = focusedNodeIds !== undefined;
  const expandedForFilter = Boolean(normalizedQuery) || filterActive;
  const automaticExpansion = expandedForFilter || focusMode;
  const matches = useMemo(() => normalizedQuery ? nodes.filter(node => `${node.label} ${node.searchText ?? ''}`.toLocaleLowerCase().includes(normalizedQuery)).map(node => node.id) : [], [nodes, normalizedQuery]);
  const matchesSet = useMemo(() => new Set(matches), [matches]);
  const required = useMemo(() => {
    const ids = new Set(matches);
    for (const id of matches) {
      let parent = forest.parents.get(id);
      while (parent) { ids.add(parent); parent = forest.parents.get(parent); }
    }
    return ids;
  }, [matches, forest]);
  const focusedNodes = useMemo(() => new Set(focusedNodeIds), [focusedNodeIds]);
  const focusedPath = useMemo(() => {
    const ids = new Set(focusedNodes);
    for (const target of [selectedId, revealId]) if (target && forest.byId.has(target)) ids.add(target);
    for (const id of [...ids]) {
      let parent = forest.parents.get(id);
      while (parent) { ids.add(parent); parent = forest.parents.get(parent); }
    }
    return ids;
  }, [focusedNodes, selectedId, revealId, forest]);
  const isExpanded = (id: string) => expandedForFilter || (focusMode ? focusedPath.has(id) : !collapsed.has(id));
  const layout = useMemo(() => {
    const positions: Position[] = [];
    const widths = new Map<string, number>();
    const visibleChildren = (id: string) => normalizedQuery
      ? (forest.children.get(id) ?? []).filter(child => required.has(child))
      : !filterActive && (focusMode ? !focusedPath.has(id) : collapsed.has(id)) ? [] : forest.children.get(id) ?? [];
    const measure = (id: string): number => {
      const children = visibleChildren(id);
      const width = Math.max(cardWidth, children.reduce((sum, child) => sum + measure(child), 0) + Math.max(0, children.length - 1) * columnGap);
      widths.set(id, width);
      return width;
    };
    const place = (id: string, left: number, depth: number) => {
      const width = widths.get(id) ?? cardWidth;
      positions.push({ id, x: left + (width - cardWidth) / 2, y: inset + depth * (cardHeight + levelGap), depth });
      const children = visibleChildren(id);
      let childLeft = left;
      for (const child of children) { place(child, childLeft, depth + 1); childLeft += (widths.get(child) ?? cardWidth) + columnGap; }
    };
    const roots = forest.roots.filter(id => !normalizedQuery || required.has(id));
    roots.forEach(measure);
    let left = inset;
    roots.forEach(id => { place(id, left, 0); left += (widths.get(id) ?? cardWidth) + columnGap; });
    const width = Math.max(cardWidth + inset * 2, left - columnGap + inset);
    const height = Math.max(cardHeight + inset * 2, ...positions.map(position => position.y + cardHeight + Math.max(levelGap, 64)));
    return { positions, width, height, byId: new Map(positions.map(position => [position.id, position])) };
  }, [forest, collapsed, normalizedQuery, filterActive, focusMode, focusedPath, required, cardWidth, cardHeight, columnGap, levelGap, inset]);
  const fittedScale = viewportSize.width && viewportSize.height
    ? Math.max(0.01, Math.min(1, (viewportSize.width - 24) / layout.width, (viewportSize.height - 64) / layout.height))
    : 1;
  const scale = fitToViewport ? fittedScale : manualScale;
  function zoomTo(nextScale: number) {
    setManualScale(nextScale);
    setFitToViewport(false);
  }

  useEffect(() => {
    const element = viewport.current;
    if (!element) return;
    const updateSize = () => {
      // hidden 패널은 마지막 크기를 보존해 보기 전환 중 배율을 잃지 않습니다.
      if (!element.clientWidth || !element.clientHeight) return;
      setViewportSize(current => current.width === element.clientWidth && current.height === element.clientHeight
        ? current : { width: element.clientWidth, height: element.clientHeight });
    };
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    updateSize();
    return () => observer.disconnect();
  }, []);

  const center = useCallback((id?: string) => {
    const element = viewport.current;
    if (!element) return;
    // 전체 맞춤은 선택 항목으로의 reveal보다 우선합니다. 배율·도면 크기가 바뀐 뒤에도
    // 특정 하위 항목을 상단으로 옮겨 전체 구조를 다시 잘라내지 않습니다.
    if (fitToViewport) {
      element.scrollTo({ left: element.clientWidth / 2, top: element.clientHeight / 2 });
      return;
    }
    const position = id ? layout.byId.get(id) : layout.positions[0];
    if (!position) return;
    const offset = element.clientWidth / 2 + Math.max(0, (element.clientWidth - layout.width * scale) / 2);
    element.scrollTo({ left: offset + (position.x + cardWidth / 2) * scale - element.clientWidth / 2, top: Math.max(0, element.clientHeight / 2 + position.y * scale - inset) });
  }, [layout, scale, cardWidth, inset, fitToViewport]);
  const revealTarget = normalizedQuery ? matches[0] : revealId;
  const previousFocusMode = useRef(focusMode);
  useEffect(() => {
    const restoringManualExpansion = previousFocusMode.current && !focusMode;
    previousFocusMode.current = focusMode;
    if (!revealTarget || focusMode || restoringManualExpansion) return;
    setCollapsed(current => {
      const next = new Set(current);
      let parent = forest.parents.get(revealTarget);
      while (parent) { next.delete(parent); parent = forest.parents.get(parent); }
      return next.size === current.size ? current : next;
    });
  }, [revealTarget, forest, focusMode]);
  useEffect(() => {
    if (revealTarget) center(revealTarget);
  }, [revealTarget, center]);
  const centeredViewport = useRef('');
  useEffect(() => {
    const viewportKey = `${viewportWidth}:${viewportSize.height}:${density}`;
    if (viewportWidth && centeredViewport.current !== viewportKey && layout.positions.length) {
      center(revealTarget ?? selectedId);
      centeredViewport.current = viewportKey;
    }
  }, [viewportWidth, viewportSize.height, density, layout.positions.length, center, revealTarget, selectedId]);

  const toggle = (id: string) => setCollapsed(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const focusNode = (id?: string) => {
    if (!id) return;
    setFocusedId(id);
    buttons.current.get(id)?.focus({ preventScroll: true });
    center(id);
  };
  const onKeyDown = (event: KeyboardEvent, id: string) => {
    const index = layout.positions.findIndex(position => position.id === id);
    if (event.key === 'ArrowDown') focusNode(layout.positions[index + 1]?.id);
    else if (event.key === 'ArrowUp') focusNode(layout.positions[index - 1]?.id);
    else if (event.key === 'Home') focusNode(layout.positions[0]?.id);
    else if (event.key === 'End') focusNode(layout.positions.at(-1)?.id);
    else if (event.key === 'ArrowLeft') {
      if (forest.children.has(id) && !collapsed.has(id) && !automaticExpansion) toggle(id);
      else focusNode(forest.parents.get(id));
    } else if (event.key === 'ArrowRight') {
      if (collapsed.has(id) && !automaticExpansion) toggle(id);
      else focusNode(forest.children.get(id)?.find(child => layout.byId.has(child)));
    } else return;
    event.preventDefault();
  };
  const allowedMove = (id: string, parentId: string) => {
    if (!onMove || id === parentId || !forest.byId.has(id) || !forest.byId.has(parentId) || forest.parents.get(id) === parentId) return false;
    let ancestor: string | undefined = parentId;
    while (ancestor) { if (ancestor === id) return false; ancestor = forest.parents.get(ancestor); }
    return !canMove || canMove(id, parentId);
  };
  const tabStop = layout.byId.has(focusedId ?? '') ? focusedId : layout.byId.has(selectedId ?? '') ? selectedId : layout.positions[0]?.id;
  // 도면이 화면 안에 들어가도 빈 공간을 잡아 이동할 수 있도록 양쪽에 탐색 여유를 둡니다.
  const stageWidth = Math.max(viewportWidth, layout.width * scale) + viewportWidth;
  const stageHeight = Math.max(viewportSize.height, layout.height * scale) + viewportSize.height;
  const endPan = (event: PointerEvent<HTMLDivElement>) => {
    const current = pan.current;
    if (!current || current.pointerId !== event.pointerId) return;
    pan.current = null;
    setPanning(false);
    if (event.currentTarget.hasPointerCapture(current.pointerId)) event.currentTarget.releasePointerCapture(current.pointerId);
  };

  return <Paper variant="outlined" data-testid="hierarchy-chart" sx={{ position: 'relative', minWidth: 0, height: fill ? '100%' : undefined, minHeight: fill ? 320 : undefined, boxSizing: 'border-box', overflow: 'hidden', borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.pageCanvas }}>
    <Box
      ref={viewport}
      role="region"
      aria-label={ariaLabel}
      sx={{ height: fill ? '100%' : { xs: 480, sm: 640 }, maxHeight: fill ? undefined : '70dvh', minHeight: fill ? 0 : 320, overflow: 'auto', overscrollBehavior: 'contain', cursor: panning ? 'grabbing' : 'grab', userSelect: panning ? 'none' : undefined }}
      onPointerDown={event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary || (event.target as Element).closest('button, a, input, textarea, select, [contenteditable="true"]')) return;
        const element = event.currentTarget;
        event.preventDefault();
        pan.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: element.scrollLeft, top: element.scrollTop };
        setPanning(true);
        element.setPointerCapture(event.pointerId);
      }}
      onPointerMove={event => {
        if (!pan.current || pan.current.pointerId !== event.pointerId) return;
        if (!(event.buttons & 1)) { endPan(event); return; }
        event.preventDefault();
        event.currentTarget.scrollLeft = pan.current.left - event.clientX + pan.current.x;
        event.currentTarget.scrollTop = pan.current.top - event.clientY + pan.current.y;
      }}
      onPointerUp={endPan}
      onPointerCancel={endPan}
      onLostPointerCapture={endPan}
      onPointerLeave={event => { if (!event.currentTarget.hasPointerCapture(event.pointerId)) endPan(event); }}
    >
      {!layout.positions.length ? <EmptyState>{normalizedQuery ? '검색 결과가 없습니다.' : '표시할 항목이 없습니다.'}</EmptyState> : <Box sx={{ width: stageWidth, height: stageHeight, position: 'relative' }}>
        <Box sx={{ position: 'absolute', left: Math.max(0, (stageWidth - layout.width * scale) / 2), top: viewportSize.height / 2, width: layout.width, height: layout.height, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <Box component="svg" aria-hidden="true" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
            {layout.positions.map(position => {
              const parent = layout.byId.get(forest.parents.get(position.id) ?? '');
              if (!parent) return null;
              const x1 = parent.x + cardWidth / 2;
              const y1 = parent.y + cardHeight;
              const x2 = position.x + cardWidth / 2;
              const y2 = position.y;
              const middle = y1 + levelGap / 2;
              return <path key={position.id} d={`M ${x1} ${y1} V ${middle} H ${x2} V ${y2}`} fill="none" stroke={selectedId === position.id ? designTokens.color.brandBorder : designTokens.color.strongDivider} strokeWidth="1.5" />;
            })}
          </Box>
          {layout.positions.map(position => {
            const node = forest.byId.get(position.id)!;
            const selected = selectedId === node.id;
            const children = forest.children.get(node.id) ?? [];
            const expanded = isExpanded(node.id);
            const dimmed = focusMode && !focusedNodes.has(node.id) && !selected && !matchesSet.has(node.id) && focusedId !== node.id;
            return <Box key={node.id} sx={{ position: 'absolute', left: position.x, top: position.y, width: cardWidth, height: cardHeight }}>
              <ButtonBase
                ref={element => { if (element) buttons.current.set(node.id, element); else buttons.current.delete(node.id); }}
                data-testid={`hierarchy-node-${node.id}`}
                aria-label={node.label}
                aria-describedby={node.description ? `${descriptionPrefix}-${node.id}` : undefined}
                aria-pressed={selected}
                aria-expanded={children.length ? expanded : undefined}
                tabIndex={tabStop === node.id ? 0 : -1}
                onFocus={() => setFocusedId(node.id)}
                onKeyDown={event => onKeyDown(event, node.id)}
                onClick={() => onSelect(node.id)}
                disableRipple
                draggable={Boolean(onMove)}
                onDragStart={event => { event.dataTransfer.setData(DRAG_TYPE, node.id); event.dataTransfer.effectAllowed = 'move'; setDraggingId(node.id); }}
                onDragEnd={() => { setDraggingId(undefined); setDropId(undefined); }}
                onDragOver={event => {
                  const external = externalDrop && event.dataTransfer.types.includes(externalDrop.type) && externalDrop.canDrop(node.id);
                  if (external || (draggingId && allowedMove(draggingId, node.id))) { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setDropId(node.id); }
                }}
                onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropId(undefined); }}
                onDrop={event => {
                  event.preventDefault();
                  if (externalDrop && event.dataTransfer.types.includes(externalDrop.type)) {
                    if (externalDrop.canDrop(node.id)) externalDrop.onDrop(node.id, event.dataTransfer.getData(externalDrop.type));
                  } else {
                    const id = event.dataTransfer.getData(DRAG_TYPE);
                    if (allowedMove(id, node.id)) onMove?.(id, node.id);
                  }
                  setDraggingId(undefined); setDropId(undefined);
                }}
                sx={{
                  width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', textAlign: 'left', p: 1.5,
                  border: '1px solid', borderColor: dropId === node.id ? designTokens.color.action : selected || node.highlighted || matchesSet.has(node.id) ? designTokens.color.brandBorder : designTokens.color.divider,
                  borderStyle: node.highlighted ? 'dashed' : 'solid', borderRadius: designTokens.radius.navigation,
                  bgcolor: selected || dropId === node.id ? designTokens.color.compactSelectedSurface : designTokens.color.canvas,
                  boxShadow: designTokens.shadow.surface, color: designTokens.color.primaryText,
                  opacity: draggingId === node.id ? 0.5 : dimmed ? designTokens.button.disabledOpacity : 1,
                  '&:hover': { bgcolor: designTokens.color.hoverSurface, borderColor: designTokens.color.brandBorder, opacity: 1 },
                  '&:focus-visible, &.Mui-focusVisible': createFocusRingStyles({ inset: true }),
                  '@media (forced-colors: active)': { borderColor: selected ? 'Highlight' : 'ButtonText' },
                }}
              >
                {node.description && <Box component="span" id={`${descriptionPrefix}-${node.id}`} sx={visuallyHiddenStyles}>{node.description}</Box>}
                <Tooltip title={node.label}><Typography component="span" sx={{ ...designTokens.typography.ui, fontWeight: 700, minHeight: 40, flexShrink: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>{node.label}</Typography></Tooltip>
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>{node.content}</Box>
                {node.footer ? <Box component="span" sx={{ display: 'block', borderTop: '1px solid', borderColor: designTokens.color.divider, flexShrink: 0, pt: compact ? 0.5 : 1, mt: compact ? 0.5 : 1, ...designTokens.typography.supporting, color: designTokens.color.secondaryText }}>{node.footer}</Box> : null}
              </ButtonBase>
              {children.length > 0 && <Tooltip title={`${node.label} 하위 항목 ${expanded ? '접기' : '펼치기'}`}><Box component="span" sx={{ display: 'inline-flex', position: 'absolute', left: '50%', top: '100%', transform: 'translate(-50%, -25%)' }}><IconButton
                size="small"
                aria-label={`${node.label} 하위 항목 ${expanded ? '접기' : '펼치기'}`}
                aria-expanded={expanded}
                disabled={automaticExpansion}
                onClick={() => toggle(node.id)}
                sx={{ bgcolor: designTokens.color.canvas, border: '1px solid', borderColor: designTokens.color.divider, '&:hover': { bgcolor: designTokens.color.hoverSurface } }}
              >{expanded ? <ExpandLessOutlined fontSize="small" /> : <ExpandMoreOutlined fontSize="small" />}</IconButton></Box></Tooltip>}
            </Box>;
          })}
        </Box>
      </Box>}
    </Box>
    <Stack direction="row" sx={{ position: 'absolute', left: 12, bottom: 12, p: 0.5, alignItems: 'center', border: '1px solid', borderColor: designTokens.color.divider, borderRadius: designTokens.radius.pill, bgcolor: designTokens.color.canvas, boxShadow: designTokens.shadow.surface }}>
      <Tooltip title="축소"><Box component="span" sx={{ display: 'inline-flex' }}><IconButton aria-label="축소" size="small" disabled={scale <= 0.1} onClick={() => zoomTo(Math.max(0.1, scale - 0.15))}><RemoveOutlined fontSize="small" /></IconButton></Box></Tooltip>
      <Typography variant="caption" role="status" aria-label="확대 비율" sx={{ width: 40, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{Math.round(scale * 100)}%</Typography>
      <Tooltip title="확대"><Box component="span" sx={{ display: 'inline-flex' }}><IconButton aria-label="확대" size="small" disabled={scale >= 1.6} onClick={() => zoomTo(Math.min(1.6, scale + 0.15))}><AddOutlined fontSize="small" /></IconButton></Box></Tooltip>
      <Tooltip title="화면에 맞추기"><Box component="span" sx={{ display: 'inline-flex' }}><IconButton aria-label="화면에 맞추기" size="small" onClick={() => { setFitToViewport(true); viewport.current?.scrollTo({ left: viewportWidth / 2, top: viewportSize.height / 2 }); }}><CenterFocusStrongOutlined fontSize="small" /></IconButton></Box></Tooltip>
      <Tooltip title="모두 펼치기"><Box component="span" sx={{ display: 'inline-flex' }}><IconButton aria-label="모두 펼치기" size="small" disabled={automaticExpansion || !collapsed.size} onClick={() => setCollapsed(new Set())}><UnfoldMoreOutlined fontSize="small" /></IconButton></Box></Tooltip>
      <Tooltip title="모두 접기"><Box component="span" sx={{ display: 'inline-flex' }}><IconButton aria-label="모두 접기" size="small" disabled={automaticExpansion} onClick={() => { setCollapsed(new Set(forest.children.keys())); setFocusedId(forest.roots[0]); }}><UnfoldLessOutlined fontSize="small" /></IconButton></Box></Tooltip>
    </Stack>
  </Paper>;
}
